import { z } from "zod";
import { CampaignSchema } from "../useCampaign";
import { OlapScoresResultSchema } from "../olapScores/olapScoresSchema";

export const CompanySchema = z.object({
    id: z.number(),
    name: z.string(),
});

export type Company = z.infer<typeof CompanySchema>;

const MultivalueVariationSchema = z.object({
    score: z.number(),
});

const MultivalueSchema = z.object({
    score: z.number(),
    variations: z.record(z.number(), MultivalueVariationSchema).optional(),
});

export const SsqPostCommentSchema = z.object({
    id: z.string(),
    comment: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    createdBy: z.object({
        id: z.number(),
        email: z.string(),
        firstname: z.string().optional(),
        lastname: z.string().optional(),
    }),
});

export type SsqPostComment = z.infer<typeof SsqPostCommentSchema>;

export const SsqPostAttachmentSchema = z.object({
    id: z.string(),
    attachmentPath: z.string(),
    createdAt: z.string(),
});

export type SsqPostAttachment = z.infer<typeof SsqPostAttachmentSchema>;

export const sanitizeEmptyArray = (val: unknown) => {
    if (Array.isArray(val) && val.length === 1 && Array.isArray(val[0]) && val[0].length === 0) {
        return [];
    }
    return val;
};

/**
 * PHP/JSON ambiguity workaround: an associative array serialized as `[]` is
 * indistinguishable from an empty list in JSON. The API normalizer flattens
 * `completions.headings` to `[]` when no heading scores are aggregated (typical
 * case for reference-driven packaging SSQs without properties). Coerce it back
 * to an empty record so the Zod schema accepts it.
 */
export const sanitizeEmptyArrayAsRecord = (val: unknown) => {
    if (Array.isArray(val) && val.length === 0) {
        return {};
    }
    return val;
};

export const UnitConversionSchema = z.object({
    sourceValue: z.string(),
    sourceUnitCode: z.string(),
    sourceUnitAbbreviation: z.string(),
    recipeId: z.number().nullable(),
    factor: z.number(),
    source: z.string().nullable(),
});

export type UnitConversion = z.infer<typeof UnitConversionSchema>;

export const PostElementSchema = z.object({
    id: z.number(),
    name: z.string().optional(),
    unitCode: z.string().optional(),
    value: z.string(),
    ssqPropertyId: z.number(),
    ssqVersionStructureId: z.number().optional(),
    position: z.number(),
    multivalues: z.record(z.number(), MultivalueSchema).optional(),
    lastChangedAt: z.string().nullable().optional(),
    isGenericValue: z.boolean().optional(),
    unitConversion: UnitConversionSchema.nullable().optional(),
    dataListOptionCodes: z.preprocess(sanitizeEmptyArray, z.array(z.string()).optional()),
    ssqPostComments: z.preprocess(sanitizeEmptyArray, SsqPostCommentSchema.array().optional()),
    ssqPostAttachments: z.preprocess(sanitizeEmptyArray, z.array(SsqPostAttachmentSchema).optional()),
});

export type PostElement = z.infer<typeof PostElementSchema>;

export type ScoreCompletion = {
    answers: number;
    questions: number;
    result: number;
};

export const CompletionSchema: z.ZodSchema<ScoreCompletion> = z.object({
    answers: z.number(),
    questions: z.number(),
    result: z.number(),
});

export type HeadingScoreCompletion = Partial<Record<string, ScoreCompletion>>;
export const CompletionsHeadingsSchema: z.ZodSchema<HeadingScoreCompletion> = z.preprocess(
    sanitizeEmptyArrayAsRecord,
    z.record(z.string(), CompletionSchema),
);

export const PostSchema = z.object({
    id: z.number(),
    // Campaign needs to be optional as some posts don't have it (e.g. Packagings posts)
    campaign: CampaignSchema.optional(),
    ssqId: z.number().optional(),
    ssqVersionId: z.number().optional(),
    elements: z.preprocess(sanitizeEmptyArray, PostElementSchema.array().optional()),
    olapScores: OlapScoresResultSchema,
    hiddenElements: z.array(z.string()),
    organization: CompanySchema.optional(),
    completions: z.object({
        headings: CompletionsHeadingsSchema,
        ssq: CompletionSchema,
    }),
    lastChangedAt: z.string().optional(),
    duplicatedFromId: z.number().optional(),
    isLocked: z.boolean(),
    isUsedInActionPlan: z.boolean(),
    relatedActionPlansMarkedAsStale: z.boolean(),
    canViewSsq: z.boolean(),
    canAnswerSsq: z.boolean(),
    canValidateSsq: z.boolean(),
    isStale: z.boolean().optional().default(false),
});

export type Post = z.infer<typeof PostSchema>;
